import React, { useMemo, useRef } from "react";
import { useEbscoSearch } from "./EbscoSearchProvider";
import { EbscoResultRecord } from "./EbscoResultRecord";
import { EbscoGetSortOptionsResponse } from "./EbscoGetSortOptionsResponse";
import { EbscoSortOption } from "./EbscoSortOption";
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  CardHeader,
  Link,
  Pagination,
  Skeleton,
  Toolbar,
  Typography,
} from "@mui/material";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useNumberFormat } from "../../../../hooks/useNumberFormat";
import { ResultsSortControl } from "../ls/LSSearchSortControl";
import { axios } from "@tlc/ls2pac/components/AxiosInstanceManager";
import { LoaderFunctionArgs, redirect, useLoaderData } from "react-router-dom";
import { LoaderData } from "../../account/MyAccount";
import { useSkipLinks } from "../../../../hooks/useSkipLinks";

const EbscoSortOptionsHolder = (() => {
  let sortOptions: EbscoSortOption[];

  return {
    getSortOptions: async () => {
      if (!sortOptions) {
        const { data } = await axios.get<EbscoGetSortOptionsResponse>(
          `/ebsco/sorts`,
        );
        sortOptions = data.sortOptions;
      }
      return sortOptions;
    },
  };
})();

export async function EbscoResultsPageLoader({ request }: LoaderFunctionArgs) {
  const sortOptions = await EbscoSortOptionsHolder.getSortOptions();

  // validate sort param and redirect without it if invalid
  const url = new URL(request.url);
  const sortParam = url.searchParams.get("sort");
  const validSortKeys = sortOptions.map(({ id }) => id);
  if (sortParam && !validSortKeys.includes(sortParam)) {
    url.searchParams.delete("sort");
    return redirect(url.toString());
  }
  return { sortOptions };
}

export const EbscoResultsPage = () => {
  const {
    searchParams: { term, page, sort },
    setPage,
    setSort,
    totalHits,
    results,
    loadingResults,
    pageSize,
  } = useEbscoSearch();
  // TODO cleaner to pull sortOptions from useEbscoSearch instead?
  const { sortOptions } = useLoaderData() as LoaderData<
    typeof EbscoResultsPageLoader
  >;
  const { translate } = useLanguage();
  const { format } = useNumberFormat();

  const ebscoResultsHeaderRef = useRef<HTMLHeadingElement | null>(null);
  useSkipLinks([
    {
      label: translate("SkipToResults"),
      targetRef: ebscoResultsHeaderRef,
      priority: 10,
    },
  ]);

  return (
    <Box>
      <Toolbar
        sx={{
          flexWrap: "wrap",
          gap: 1,
          pl: { sm: 1, xs: 1 },
          pr: { xs: 1, sm: 1 },
        }}
      >
        <Typography
          tabIndex={-1}
          ref={ebscoResultsHeaderRef}
          color="inherit"
          variant="h6"
          component="h2"
        >
          {loadingResults ? (
            <Skeleton
              variant="text"
              width={20}
              sx={{ display: "inline-flex" }}
            />
          ) : (
            totalHits && format(totalHits)
          )}{" "}
          {translate("searchResultsFor")} "{term}"
        </Typography>
      </Toolbar>
      <Toolbar>
        <ResultsSortControl<string>
          options={sortOptions.reduce((p, { id, name }) => {
            p[id] = name;
            return p;
          }, {} as Record<string, string>)}
          sort={sort}
          loadingResults={loadingResults}
          totalHits={totalHits ?? 0}
          setSort={setSort}
          id={"ebsco-sort"}
        />
      </Toolbar>
      {loadingResults ? (
        <>
          {Array.from({ length: pageSize }).map((_, index) => (
            <div key={index}>
              <EbscoResultSkeleton />
            </div>
          ))}
        </>
      ) : (
        <>
          {totalHits && totalHits > 0 ? (
            results &&
            results.map((result) => (
              <div key={result.recordId}>
                <EbscoResultDisplay record={result} />
              </div>
            ))
          ) : (
            <Alert severity="info">
              <AlertTitle>{translate("searchErrorMessage")}</AlertTitle>
            </Alert>
          )}
        </>
      )}
      {(totalHits ?? 0) > pageSize && (
        <Pagination
          page={page}
          count={Math.ceil((totalHits ?? 0) / pageSize)}
          onChange={(_: React.ChangeEvent<unknown>, value: number) => {
            setPage(value);
          }}
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            paddingTop: "30px",
            paddingBottom: "50px",
          }}
        />
      )}
    </Box>
  );
};

export const EbscoResultSkeleton = () => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Skeleton
            animation="wave"
            height={10}
            width="80%"
            style={{ marginBottom: 6 }}
          />
        }
        subheader={<Skeleton animation="wave" height={10} width="40%" />}
      />
      <CardContent>
        <React.Fragment>
          <Skeleton animation="wave" height={10} style={{ marginBottom: 6 }} />
          <Skeleton animation="wave" height={10} width="80%" />
        </React.Fragment>
      </CardContent>
    </Card>
  );
};

interface EbscoResultDisplayProps {
  record: EbscoResultRecord;
}
export const EbscoResultDisplay: React.FC<EbscoResultDisplayProps> = ({
  record,
}) => {
  const { translate } = useLanguage();
  const { articleInfo, externalLinks, pubType } = record;
  const title = articleInfo.title || "There is no title";
  const link = useMemo(() => {
    return externalLinks.find(({ type }) => type === "persistentLink")?.url;
  }, [externalLinks]);

  //   h2 typography component
  const titleLink = useMemo(() => {
    if (link) {
      return (
        <Link href={link} target="_blank" rel="noopener noreferrer">
          {title}
        </Link>
      );
    }
    return title;
  }, [link, title]);

  const author = useMemo(() => {
    if (pubType === "Author") {
      return null;
    }
    if (articleInfo.authors?.length) {
      return (
        <Typography component="h3">
          {translate("by")} {articleInfo.authors[0]}
        </Typography>
      );
    }
  }, [articleInfo, pubType]);

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        slotProps={{
          title: {
            component: "h2",
          },
        }}
        title={titleLink}
        subheader={
          <>
            {author}
            <EbscoArticleInfo record={record} />
          </>
        }
      />
      <CardContent>
        <Typography>{articleInfo.summary}</Typography>
      </CardContent>
    </Card>
  );
};

export function EbscoArticleInfo({ record }: { record: EbscoResultRecord }) {
  const { translate } = useLanguage();
  const spacer = ", ";

  function buildSeriesInfo(data: EbscoResultRecord) {
    let response = translate("series") + " - ";
    if (data.publicationInfo?.date) {
      response += data.publicationInfo.date;
    }
    // TODO from legacy: add audience range? (needs backend support)
    return response;
  }

  function buildAuthorInfo(_data: EbscoResultRecord) {
    return translate("authorRecord");
  }

  function buildBookInfo(data: EbscoResultRecord) {
    let response = "";
    if (data.publicationInfo?.publisher) {
      response += data.publicationInfo.publisher;
      response += spacer;
    }
    if (data.publicationInfo?.date) {
      response += data.publicationInfo.date;
    }
    return response;
  }

  function buildReviewInfo(data: EbscoResultRecord) {
    return data.articleInfo?.docType ?? "";
  }

  function buildPeriodicalInfo(data: EbscoResultRecord) {
    let response = "";

    if (data.journalInfo?.title) {
      response += data.journalInfo.title;
      response += spacer;
    }
    if (data.publicationInfo?.date) {
      response += data.publicationInfo.date;
      response += spacer;
    }
    if (data.publicationInfo?.volume) {
      response += translate("vol") + " " + data.publicationInfo.volume;
      response += spacer;
    }
    if (data.publicationInfo?.issue) {
      response += translate("issue") + " " + data.publicationInfo.issue;
      response += spacer;
    }
    if (data.articleInfo?.pageCount) {
      const count = parseInt(data.articleInfo.pageCount, 10);
      if (Number.isFinite(count)) {
        if (data.articleInfo?.pageFrom) {
          const start = parseInt(data.articleInfo.pageFrom, 10);
          if (Number.isFinite(start)) {
            response +=
              translate("pageAbbreviation") + start + "-" + (start + count);

            response += spacer;
          }
        }

        response += count + translate("pageAbbreviation");
      }
    }

    return response;
  }

  function buildGenericInfo(data: EbscoResultRecord) {
    let response = "";
    if (data.publicationInfo?.date) {
      response += data.publicationInfo.date;
      response += " - ";
    }
    if (data.journalInfo?.title) {
      response += data.journalInfo.title;
    }
    return response;
  }

  function buildEncyclopediaInfo(data: EbscoResultRecord) {
    let response = "";
    if (data.headerInfo?.longDbName) {
      response += data.headerInfo.longDbName;
    }
    return response;
  }

  function getEbscoArticleInfo(data: EbscoResultRecord) {
    let response = "";
    switch (data.pubType) {
      case "Series":
        response = buildSeriesInfo(data);
        break;
      case "Author":
        response = buildAuthorInfo(data);
        break;
      case "Book":
        response = buildBookInfo(data);
        break;
      case "Review":
        response = buildReviewInfo(data);
        break;
      case "Periodical":
        response = buildPeriodicalInfo(data);
        break;
      case "WIRES":
      case "NEWSP":
      case "TSRPT":
        response = buildGenericInfo(data);
        break;
      case "Encyclopedia":
        response = buildEncyclopediaInfo(data);
        break;
      default:
        response = translate("articleRecord");
        break;
    }
    return response;
  }

  const response = useMemo(() => {
    return getEbscoArticleInfo(record);
  }, [record.recordId, translate]);

  return <Typography component={"h4"}>{response}</Typography>;
}
